import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from 'firebase/auth';

// VITE_FIREBASE_* vars are resolved per-app at build time, so even though this
// file lives in @pm/core, each app (Freddie, Frida) gets its own Firebase project values.
const getFirebase = () => {
  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
  const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  return { auth: getAuth(app), provider: new GoogleAuthProvider() };
};

// though it's recommended on Firebase docs, `signInWithRedirect`
// should never be used: it's broken on Safari 16.1+, Chrome 115+,
// and Firefox 109+ due to third-party storage partitioning (ITP).
// `signInWithPopup` works reliably across all browsers
export const signInWithGoogle = async (): Promise<string> => {
  const { auth, provider } = getFirebase();
  provider.setCustomParameters({ prompt: 'select_account' });
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user.getIdToken();
  } catch (e) {
    // Safari ITP intermittently drops the sessionStorage state the popup's
    // /__/auth/handler page needs, surfacing as auth/missing-initial-state.
    // It doesn't reproduce on a second attempt, so retry once before giving up.
    if ((e as { code?: string }).code === 'auth/missing-initial-state') {
      const result = await signInWithPopup(auth, provider);
      return result.user.getIdToken();
    }
    throw e;
  }
};

// called after redirect_to_intake so the next signInWithGoogle starts fresh
// without a cached Firebase credential that would bypass the account picker
export const signOutFromGoogle = async (): Promise<void> => {
  const { auth } = getFirebase();
  await signOut(auth);
};
